import type { SignableAction } from '../types';

export type SimulatedCertPurpose = 'identity' | 'signing' | 'encryption';

/**
 * Where a credential lives. `cac` certs are read off a physical smart card
 * (insert + card PIN). `windows` certs live in the Windows certificate store
 * and are reached via CryptoAPI/CNG — no card, OS-mediated key access instead.
 */
export type CertProvider = 'cac' | 'windows';

/**
 * How a Windows-store private key is guarded. Drives the "Windows Security"
 * step: a plain consent, a strong-key password prompt, or a Windows Hello
 * verification. Ignored for `cac` certs (those use the card PIN flow).
 */
export type WindowsKeyProtection = 'none' | 'password' | 'hello';

export type SimulatedCert = {
  id: string;
  provider: CertProvider;
  purpose: SimulatedCertPurpose;
  subjectCN: string;
  issuerCN: string;
  notAfter: string;
  keyUsage: string;
  validForActions: SignableAction[];
  /** Windows-store certs only: friendly name shown in the OS cert picker. */
  friendlyName?: string;
  /** Windows-store certs only: how the private key is protected. */
  keyProtection?: WindowsKeyProtection;
};

export type SimStep =
  | 'insert'
  | 'pick-cert'
  | 'enter-pin'
  | 'signing'
  | 'verifying';

export type SimulationErrorCode =
  | 'NO_CARD'
  | 'WRONG_PIN'
  | 'PIN_LOCKED'
  | 'KEY_ACCESS_DENIED'
  | 'USER_CANCELLED';

export class SimulationError extends Error {
  code: SimulationErrorCode;
  constructor(code: SimulationErrorCode, message?: string) {
    super(message ?? code);
    this.code = code;
    this.name = 'SimulationError';
  }
}

export type OpenSessionArgs = {
  action: SignableAction;
  certs?: SimulatedCert[];
};

export type SessionResult = {
  cert: SimulatedCert;
  pin: string;
};
